import { FC, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { NavItem, Link } from "./Navigation";
import Text from "@/components/Text";
import { hexToRgba } from "@/utils/theme";
import NavigationItemsCount from "./NavigationItemsCount";

const SubMenuLink = styled.div`
  display: flex;
  svg {
    flex-shrink: 0;
    transition: 0.2s ease-out transform;
  }
  ${Text} {
    opacity: 0.6;
    transition: 0.2s ease-out opacity;
  }
  cursor: pointer;
  &:hover,
  &.active {
    ${Text} {
      opacity: 1;
    }
  }
  svg {
    margin-right: 8px;
  }
`;

const Li = styled.li`
  margin: 0 0 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubMenuLinkInner = styled.div<{ $open?: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  svg {
    flex-shrink: 0;
    margin-left: 8px;
  }
  ${({ $open }) =>
    $open &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const Ul = styled.ul`
  margin: 8px 0 -9px 8px;
  padding: 16px 0 16px 17px;
  border-left: solid 1px ${({ theme }) => hexToRgba(theme.colors.white, 0.08)};
`;

interface Props {
  navItem: NavItem;
}

const NavigationSubMenu: FC<Props> = ({ navItem }) => {
  const [subMenuOpen, setSubMenuOpen] = useState(false);

  const { teamId } = useParams();

  const location = useLocation();

  useEffect(() => {
    const isCurrentLocation = navItem.navItems!.find((element) => {
      return location.pathname === `/teams/${teamId}/${element.to}`;
    });
    if (isCurrentLocation) setSubMenuOpen(true);
  }, [location.pathname, teamId, navItem.navItems]);

  return (
    <>
      <SubMenuLink onClick={() => setSubMenuOpen(!subMenuOpen)}>
        {!!navItem.Icon && <navItem.Icon />}
        <SubMenuLinkInner $open={subMenuOpen}>
          <Text as="span" $color="white" $type="smallBold">
            {navItem.title}
          </Text>
          <svg
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.6"
              d="M1.5 1L5 4.5L8.5 1"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SubMenuLinkInner>
      </SubMenuLink>
      {subMenuOpen && (
        <Ul>
          {navItem.navItems!.map((subItem, index) => (
            <Li key={index}>
              <Link
                end={subItem.end}
                to={
                  subItem.general
                    ? `/general/${subItem.to}`
                    : `/teams/${teamId}/${subItem.to}`
                }
                className={(isActive) => isActive && "active"}
              >
                <Text as="span" $color="white" $type="smallBold">
                  {subItem.title}
                </Text>
              </Link>
              {!!subItem.count && (
                <NavigationItemsCount count={subItem.count} />
              )}
            </Li>
          ))}
        </Ul>
      )}
    </>
  );
};

export default NavigationSubMenu;
